import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { timeAgo as fmt } from '../../utils/formatters'

const CHANNELS = [
  { id: 'general', label: 'General', business: null },
  { id: 'pools', label: 'CP Quality Pools', business: 'pools' },
  { id: 'riverbend', label: 'Riverbend Clearing', business: 'riverbend' },
]

export default function ChatPage() {
  const { profile } = useAuth()
  const [activeChannel, setActiveChannel] = useState('general')
  const [activeDM, setActiveDM] = useState(null)
  const [messages, setMessages] = useState([])
  const [users, setUsers] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showScoreboard, setShowScoreboard] = useState(false)
  const [scores, setScores] = useState([])
  const [selectedCrew, setSelectedCrew] = useState(null)
  const bottomRef = useRef(null)
  const role = profile?.role
  const isStore = role === 'store_partner'

  const visibleChannels = CHANNELS.filter(c => {
    if (isStore) return false
    if (!c.business) return true
    if (role === 'owner') return true
    return profile?.business_ids?.includes(c.business)
  })

  const fetchUsers = async () => {
    const { data } = await supabase.from('profiles').select('id, full_name, role').eq('is_active', true).not('role', 'eq', 'store_partner')
    setUsers(data || [])
  }

  const fetchMessages = async (channel, dmId) => {
    setLoading(true)
    let query = supabase.from('messages').select('*, profiles!messages_sender_id_fkey(full_name)').order('created_at', { ascending: true }).limit(100)
    if (dmId) {
      query = query.or(`and(sender_id.eq.${profile?.id},recipient_id.eq.${dmId}),and(sender_id.eq.${dmId},recipient_id.eq.${profile?.id})`)
    } else {
      query = query.eq('channel', channel).is('recipient_id', null)
    }
    const { data } = await query
    setMessages(data || [])
    setLoading(false)
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  useEffect(() => { if (!isStore) fetchUsers() }, [isStore]) // eslint-disable-line
  useEffect(() => { if (!isStore) fetchMessages(activeChannel, activeDM) }, [activeChannel, activeDM, isStore]) // eslint-disable-line

  useEffect(() => {
    if (isStore) return
    const sub = supabase.channel('messages-live')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, (payload) => {
        const msg = payload.new
        const isForThisView = activeDM
          ? (msg.sender_id === activeDM && msg.recipient_id === profile?.id) || (msg.sender_id === profile?.id && msg.recipient_id === activeDM)
          : msg.channel === activeChannel && !msg.recipient_id
        if (isForThisView) fetchMessages(activeChannel, activeDM)
      })
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [activeChannel, activeDM, profile, isStore]) // eslint-disable-line

  const handleSend = async () => {
    if (!input.trim() || sending) return
    setSending(true)
    const { error } = await supabase.from('messages').insert({
      sender_id: profile?.id,
      channel: activeDM ? 'dm' : activeChannel,
      recipient_id: activeDM || null,
      business_id: CHANNELS.find(c => c.id === activeChannel)?.business || null,
      content: input.trim(),
    })
    if (!error) { setInput(''); fetchMessages(activeChannel, activeDM) }
    setSending(false)
  }

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }

  const otherUsers = users.filter(u => u.id !== profile?.id)
  const activeLabel = activeDM ? users.find(u => u.id === activeDM)?.full_name : CHANNELS.find(c => c.id === activeChannel)?.label

  var fetchScoreboard = async function() {
    var { data: entries } = await supabase.from('job_time_entries').select('*, profiles(full_name), jobs(job_type, pool_dimensions, estimated_hours)').not('clock_out', 'is', null).order('clock_out', { ascending: false })
    if (!entries) { setScores([]); return }
    var byUser = {}
    entries.forEach(function(e) {
      var uid = e.user_id; var name = e.profiles?.full_name || 'Unknown'
      if (!byUser[uid]) byUser[uid] = { name: name, jobs: [], totalHours: 0, totalExpected: 0 }
      var expected = e.jobs?.estimated_hours || 0; var actual = e.hours_worked || 0
      byUser[uid].jobs.push({ actual: actual, expected: expected, jobType: e.jobs?.job_type, poolSize: e.jobs?.pool_dimensions, date: e.clock_out })
      byUser[uid].totalHours += actual
      if (expected > 0) byUser[uid].totalExpected += expected
    })
    var scoreArr = Object.keys(byUser).map(function(uid) {
      var u = byUser[uid]; var jobsWithStd = u.jobs.filter(function(j) { return j.expected > 0 })
      var avgPct = jobsWithStd.length > 0 ? Math.round(jobsWithStd.reduce(function(s, j) { return s + (j.actual / j.expected) * 100 }, 0) / jobsWithStd.length) : null
      var fastest = jobsWithStd.length > 0 ? Math.min.apply(null, jobsWithStd.map(function(j) { return j.actual / j.expected * 100 })) : null
      return { uid: uid, name: u.name, totalJobs: u.jobs.length, totalHours: u.totalHours, avgPct: avgPct, fastest: fastest, jobs: u.jobs }
    }).sort(function(a, b) { return (a.avgPct || 999) - (b.avgPct || 999) })
    setScores(scoreArr)
  }

  if (isStore) {
    return (
      <div style={{padding:'60px',textAlign:'center',color:'rgba(255,255,255,0.3)',fontFamily:"'Barlow',sans-serif"}}>
        Messages are not available for store partners.
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <div style={styles.sidebarTitle}>CHANNELS</div>
        {visibleChannels.map(ch => (
          <button key={ch.id} onClick={() => { setActiveChannel(ch.id); setActiveDM(null); setShowScoreboard(false) }} style={{...styles.channelBtn,...(activeChannel===ch.id&&!activeDM&&!showScoreboard?styles.channelBtnActive:{})}}>
            # {ch.label}
          </button>
        ))}
        <button onClick={function(){setShowScoreboard(true);setActiveDM(null);fetchScoreboard()}} style={{...styles.channelBtn,...(showScoreboard?styles.channelBtnActive:{}),color:'#b48c3c',marginTop:'8px',marginBottom:'8px'}}>
          {String.fromCodePoint(0x1F3C6)} CREW SCOREBOARD
        </button>
        <div style={styles.sidebarTitle}>DIRECT MESSAGES</div>
        {otherUsers.map(u => (
          <button key={u.id} onClick={() => { setActiveDM(u.id); setActiveChannel(null); setShowScoreboard(false) }} style={{...styles.channelBtn,...(activeDM===u.id?styles.channelBtnActive:{})}}>
            <div style={styles.dmAvatar}>{u.full_name?.[0]}</div>
            {u.full_name}
          </button>
        ))}
      </div>

      <div style={styles.main}>
        {showScoreboard ? (
          <div style={{padding:'24px',overflow:'auto',flex:1}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'22px',fontWeight:700,letterSpacing:'3px',color:'#b48c3c',marginBottom:'20px'}}>{String.fromCodePoint(0x1F3C6)} CREW SCOREBOARD</div>
            {scores.length === 0 ? <div style={{color:'rgba(255,255,255,0.3)',padding:'40px',textAlign:'center'}}>No completed time entries yet. Clock in/out on pool jobs to start tracking.</div> : (
              <div>
                {!selectedCrew ? (
                  <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
                    {scores.map(function(s, rank) {
                      var pctColor = s.avgPct === null ? 'rgba(255,255,255,0.3)' : s.avgPct <= 95 ? '#4a8c6a' : s.avgPct <= 110 ? '#b48c3c' : '#e05c5c'
                      return <div key={s.uid} onClick={function(){setSelectedCrew(s)}} style={{display:'flex',alignItems:'center',gap:'16px',padding:'16px 20px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',cursor:'pointer'}}>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'24px',fontWeight:700,color:rank===0?'#b48c3c':'rgba(255,255,255,0.3)',width:'30px',textAlign:'center'}}>{rank+1}</div>
                        <div style={{flex:1}}><div style={{fontSize:'16px',fontWeight:600,color:'#ffffff'}}>{s.name}</div><div style={{fontSize:'12px',color:'rgba(255,255,255,0.35)',marginTop:'2px'}}>{s.totalJobs} jobs | {s.totalHours.toFixed(1)} total hrs</div></div>
                        <div style={{textAlign:'right'}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'24px',fontWeight:700,color:pctColor}}>{s.avgPct !== null ? s.avgPct + '%' : '--'}</div><div style={{fontSize:'10px',color:'rgba(255,255,255,0.25)',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'1px'}}>AVG OF EXPECTED</div></div>
                        {s.fastest !== null && <div style={{textAlign:'right',marginLeft:'12px'}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'16px',fontWeight:700,color:'#4a8c6a'}}>{Math.round(s.fastest)}%</div><div style={{fontSize:'9px',color:'rgba(255,255,255,0.25)',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'1px'}}>BEST</div></div>}
                      </div>
                    })}
                  </div>
                ) : (
                  <div>
                    <button onClick={function(){setSelectedCrew(null)}} style={{padding:'8px 16px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer',marginBottom:'16px'}}>BACK TO SCOREBOARD</button>
                    <div style={{fontSize:'20px',fontWeight:700,color:'#ffffff',marginBottom:'16px'}}>{selectedCrew.name}</div>
                    <div style={{display:'flex',gap:'16px',marginBottom:'20px',flexWrap:'wrap'}}>
                      <div style={{padding:'16px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',textAlign:'center',flex:1}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'22px',fontWeight:700,color:'#ffffff'}}>{selectedCrew.totalJobs}</div><div style={{fontSize:'9px',color:'rgba(255,255,255,0.3)',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'1.5px'}}>JOBS</div></div>
                      <div style={{padding:'16px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',textAlign:'center',flex:1}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'22px',fontWeight:700,color:(selectedCrew.avgPct||0)<=95?'#4a8c6a':(selectedCrew.avgPct||0)<=110?'#b48c3c':'#e05c5c'}}>{selectedCrew.avgPct||'--'}%</div><div style={{fontSize:'9px',color:'rgba(255,255,255,0.3)',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'1.5px'}}>AVG</div></div>
                      <div style={{padding:'16px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',textAlign:'center',flex:1}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'22px',fontWeight:700,color:'#4a8c6a'}}>{selectedCrew.fastest?Math.round(selectedCrew.fastest)+'%':'--'}</div><div style={{fontSize:'9px',color:'rgba(255,255,255,0.3)',fontFamily:"'Barlow Condensed',sans-serif",letterSpacing:'1.5px'}}>BEST</div></div>
                    </div>
                    <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'2px',color:'#b48c3c',marginBottom:'10px'}}>RECENT JOBS</div>
                    {selectedCrew.jobs.slice(0,20).map(function(j,i) {
                      var pct = j.expected > 0 ? Math.round(j.actual/j.expected*100) : null
                      var c = pct===null?'rgba(255,255,255,0.3)':pct<=95?'#4a8c6a':pct<=110?'#b48c3c':'#e05c5c'
                      return <div key={i} style={{display:'flex',justifyContent:'space-between',padding:'8px 12px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'3px',marginBottom:'3px',fontSize:'13px'}}>
                        <span style={{color:'rgba(255,255,255,0.5)'}}>{j.poolSize||j.jobType||'Pool Job'}</span>
                        <span style={{color:'rgba(255,255,255,0.4)'}}>{j.actual.toFixed(1)}h{j.expected?' / '+j.expected+'h':''}</span>
                        <span style={{color:c,fontWeight:600}}>{pct!==null?pct+'%':'--'}</span>
                      </div>
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
        <div style={styles.mainHeader}>
          <div style={styles.mainTitle}>{activeDM ? '@ ' : '# '}{activeLabel}</div>
        </div>
        )}
        {!showScoreboard && <div style={styles.messageList}>
          {loading ? (
            <div style={styles.loadingText}>Loading messages...</div>
          ) : messages.length === 0 ? (
            <div style={styles.emptyText}>No messages yet. Say something!</div>
          ) : (
            messages.map((msg, i) => {
              const isMe = msg.sender_id === profile?.id
              const showName = i === 0 || messages[i-1].sender_id !== msg.sender_id
              return (
                <div key={msg.id} style={{...styles.messageRow,...(isMe?styles.messageRowMe:{})}}>
                  {!isMe && showName && <div style={styles.senderAvatar}>{msg.profiles?.full_name?.[0]}</div>}
                  {!isMe && !showName && <div style={styles.avatarSpacer}/>}
                  <div style={styles.messageBubbleWrap}>
                    {showName && !isMe && <div style={styles.senderName}>{msg.profiles?.full_name}</div>}
                    <div style={{...styles.bubble,...(isMe?styles.bubbleMe:styles.bubbleThem)}}>{msg.content}</div>
                    <div style={{...styles.timestamp,...(isMe?{textAlign:'right'}:{})}}>{fmt(msg.created_at)}</div>
                  </div>
                </div>
              )
            })
          )}
          <div ref={bottomRef} />
        </div>}
        {!showScoreboard && <div style={styles.inputRow}>
          <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey} style={styles.input} placeholder={`Message ${activeLabel}...`} rows={1} />
          <button onClick={handleSend} disabled={sending || !input.trim()} style={styles.sendBtn}>SEND</button>
        </div>}
      </div>
    </div>
  )
}

const styles = {
  container:{display:'flex',height:'calc(100vh - 80px)',fontFamily:"'Barlow',sans-serif",overflow:'hidden'},
  sidebar:{width:'220px',flexShrink:0,backgroundColor:'rgba(255,255,255,0.02)',borderRight:'1px solid rgba(255,255,255,0.06)',display:'flex',flexDirection:'column',overflowY:'auto',padding:'16px 0'},
  sidebarTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',letterSpacing:'2px',color:'rgba(255,255,255,0.25)',padding:'16px 16px 8px'},
  channelBtn:{display:'flex',alignItems:'center',gap:'8px',width:'100%',padding:'8px 16px',backgroundColor:'transparent',border:'none',color:'rgba(255,255,255,0.45)',fontFamily:"'Barlow',sans-serif",fontSize:'14px',cursor:'pointer',textAlign:'left'},
  channelBtnActive:{backgroundColor:'rgba(180,140,60,0.1)',color:'#ffffff'},
  dmAvatar:{width:'24px',height:'24px',borderRadius:'50%',backgroundColor:'rgba(180,140,60,0.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',color:'#b48c3c',flexShrink:0},
  main:{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'},
  mainHeader:{padding:'16px 24px',borderBottom:'1px solid rgba(255,255,255,0.06)'},
  mainTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,letterSpacing:'2px',color:'#ffffff'},
  messageList:{flex:1,overflowY:'auto',padding:'20px 24px',display:'flex',flexDirection:'column',gap:'4px'},
  loadingText:{color:'rgba(255,255,255,0.25)',textAlign:'center',padding:'40px'},
  emptyText:{color:'rgba(255,255,255,0.2)',textAlign:'center',padding:'40px',fontSize:'14px'},
  messageRow:{display:'flex',gap:'10px',alignItems:'flex-end',marginBottom:'2px'},
  messageRowMe:{flexDirection:'row-reverse'},
  senderAvatar:{width:'32px',height:'32px',borderRadius:'50%',backgroundColor:'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',color:'rgba(255,255,255,0.6)',flexShrink:0},
  avatarSpacer:{width:'32px',flexShrink:0},
  messageBubbleWrap:{display:'flex',flexDirection:'column',maxWidth:'65%'},
  senderName:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',letterSpacing:'1px',color:'rgba(255,255,255,0.3)',marginBottom:'4px',marginLeft:'2px'},
  bubble:{padding:'10px 14px',borderRadius:'12px',fontSize:'15px',lineHeight:1.5,wordBreak:'break-word'},
  bubbleThem:{backgroundColor:'rgba(255,255,255,0.06)',color:'#ffffff',borderBottomLeftRadius:'3px'},
  bubbleMe:{backgroundColor:'rgba(180,140,60,0.2)',color:'#ffffff',borderBottomRightRadius:'3px'},
  timestamp:{fontSize:'11px',color:'rgba(255,255,255,0.2)',marginTop:'3px',marginLeft:'2px'},
  inputRow:{display:'flex',gap:'10px',padding:'16px 24px',borderTop:'1px solid rgba(255,255,255,0.06)',alignItems:'flex-end'},
  input:{flex:1,backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',padding:'12px 16px',color:'#ffffff',fontSize:'15px',fontFamily:"'Barlow',sans-serif",outline:'none',resize:'none',lineHeight:1.5},
  sendBtn:{backgroundColor:'#b48c3c',border:'none',borderRadius:'8px',padding:'12px 20px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor:'pointer',flexShrink:0},
}
